import { useEffect, useState } from "react";
import api from "@/lib/api";
import { fmtDateTime } from "@/lib/format";
import { ShieldCheck } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

export default function AuditLogs() {
  const [logs, setLogs] = useState([]);
  const [role, setRole] = useState("all");

  useEffect(() => {
    const qs = role === "all" ? "" : `?role=${role}`;
    api.get(`/admin/audit-logs${qs}`).then((r) => setLogs(r.data.logs)).catch(() => {});
  }, [role]);

  return (
    <div data-testid="audit-logs-page" className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-2xl font-semibold tracking-tight">Audit Logs</h1>
          <p className="text-sm text-muted-foreground">Immutable record of every important action. Read-only — entries cannot be edited or deleted from the UI.</p>
        </div>
        <Select value={role} onValueChange={setRole}>
          <SelectTrigger data-testid="audit-role-filter" className="w-44 bg-white"><SelectValue /></SelectTrigger>
          <SelectContent className="bg-white">
            <SelectItem value="all">All actors</SelectItem>
            <SelectItem value="buyer">Buyer</SelectItem>
            <SelectItem value="supplier">Supplier</SelectItem>
            <SelectItem value="org_admin">Admin</SelectItem>
            <SelectItem value="system">System</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="divide-y rounded-lg border bg-card" data-testid="audit-log-list">
        {logs.length === 0 && <p className="p-6 text-sm text-muted-foreground">No audit entries yet. Actions like reminders, status updates, modifications and admin changes are recorded here.</p>}
        {logs.map((l, i) => (
          <div key={l.id} data-testid={`audit-row-${i}`} className="flex items-start gap-3 px-5 py-3.5">
            <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-slate-400" />
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-2 text-sm">
                <span className="font-medium capitalize">{l.actor_role?.replace("_", " ")}: {l.actor_name}</span>
                <span className="text-muted-foreground">·</span>
                <span>{l.action}</span>
                {l.po_number && <span className="font-mono text-xs font-semibold text-slate-700">{l.po_number}</span>}
                {l.channel && <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-semibold uppercase">{l.channel}</span>}
              </div>
              {(l.old_value || l.new_value || l.reason) && (
                <p className="mt-1 text-xs text-muted-foreground">
                  {l.old_value && <>Old: <span className="font-mono">{l.old_value}</span> </>}
                  {l.new_value && <>→ New: <span className="font-mono">{l.new_value}</span> </>}
                  {l.reason && <>· Reason: {l.reason}</>}
                </p>
              )}
            </div>
            <span className="shrink-0 font-mono text-xs text-slate-400">{fmtDateTime(l.created_at)}</span>
          </div>
        ))}
      </div>
    </div>
  );
}
